import { motion } from "framer-motion";
import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import moment from "moment";
import {
    MdListAlt,
    MdRefresh,
    MdCalendarToday,
    MdAccountBalance,
    MdHourglassEmpty,
    MdCheckCircle,
    MdSavings,
} from "react-icons/md";
import { BASE_API_URL } from "../helpers/Config";
import { getPageStyles, MOTION } from "../helpers/pageTheme";

const HERO_GRADIENT =
    "linear-gradient(135deg, #059669 0%, #047857 55%, #065f46 100%)";

const PAGE_LIMIT = 10;

const defaultMeta = {
    total: 0,
    limit: PAGE_LIMIT,
    page_no: 1,
    count: 0,
    has_more: false,
    total_page: 0,
};

/** API may return error: false (boolean) on success */
const getApiError = (payload) => {
    if (!payload) return "Invalid response";
    if (payload.error === "session expired") return "session expired";
    if (payload.error === false || payload.error === null || payload.error === undefined) {
        return null;
    }
    if (payload.error === true) return payload?.message || "Request failed";
    if (typeof payload.error === "string") return payload.error;
    return null;
};

const RecoveryTradeList = ({ theme = "dark" }) => {
    const [trades, setTrades] = useState([]);
    const [meta, setMeta] = useState(defaultMeta);
    const [pageNo, setPageNo] = useState(1);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const navigate = useNavigate();
    const loadMoreRef = useRef(null);
    const fetchIdRef = useRef(0);
    const isDark = theme === "dark";
    const styles = useMemo(() => getPageStyles(isDark), [isDark]);

    const formatAmount = useCallback((amount) => {
        const n = Number(amount);
        if (isNaN(n)) return "$0";
        return `$${n.toLocaleString("en-US", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        })}`;
    }, []);

    const fetchList = useCallback(
        async ({ page = 1, append = false, isRefresh = false } = {}) => {
            const token = localStorage.getItem("a5srf4wer7w1cd");
            if (!token) {
                setError("Please login again");
                setLoading(false);
                return;
            }

            const requestId = ++fetchIdRef.current;

            if (append) setLoadingMore(true);
            else if (isRefresh) setRefreshing(true);
            else setLoading(true);
            setError(null);

            try {
                const response = await axios.post(
                    `${BASE_API_URL}/recovery-trade/list`,
                    { token, page_no: page, limit: PAGE_LIMIT }
                );
                if (requestId !== fetchIdRef.current) return;

                const payload = response?.data;
                const apiError = getApiError(payload);

                if (apiError === "session expired") {
                    localStorage.removeItem("a5srf4wer7w1cd");
                    navigate("/login");
                    return;
                }

                if (apiError) {
                    setError(apiError);
                    toast.error(apiError);
                    return;
                }

                const list = Array.isArray(payload?.data) ? payload.data : [];
                setTrades((prev) => (append ? [...prev, ...list] : list));
                setMeta({ ...defaultMeta, ...(payload?.meta || {}) });
                setPageNo(page);
            } catch (err) {
                if (requestId !== fetchIdRef.current) return;
                const raw =
                    err.response?.data?.message || err.response?.data?.error;
                const errText =
                    typeof raw === "string" ? raw : "Failed to load recovery trade list";
                setError(errText);
                toast.error("Failed to load list");
            } finally {
                if (requestId !== fetchIdRef.current) return;
                setLoading(false);
                setLoadingMore(false);
                setRefreshing(false);
            }
        },
        [navigate]
    );

    useEffect(() => {
        fetchList({ page: 1, append: false });
    }, [fetchList]);

    const handleRefresh = () => fetchList({ page: 1, append: false, isRefresh: true });

    const loadMore = useCallback(() => {
        if (!meta.has_more || loading || loadingMore) return;
        fetchList({ page: pageNo + 1, append: true });
    }, [meta.has_more, loading, loadingMore, pageNo, fetchList]);

    useEffect(() => {
        const sentinel = loadMoreRef.current;
        if (!sentinel || !meta.has_more || loading || loadingMore) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) loadMore();
            },
            { root: null, rootMargin: "120px", threshold: 0 }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [meta.has_more, loading, loadingMore, loadMore, trades.length]);

    const amountFields = [
        { key: "amount", label: "Amount", icon: MdAccountBalance, accent: "#6366f1" },
        { key: "pending_amount", label: "Pending", icon: MdHourglassEmpty, accent: "#f59e0b" },
        { key: "claimed_amount", label: "Claimed", icon: MdCheckCircle, accent: "#22c55e" },
        { key: "unclaimed_amount", label: "Unclaimed", icon: MdSavings, accent: "#14b8a6" },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.35 }}
            className="px-3 py-3 overflow-auto"
            style={styles.page}
        >
            <motion.div
                variants={MOTION.container}
                initial="hidden"
                animate="visible"
                className="d-flex flex-column gap-3"
                style={{ maxWidth: 480, margin: "0 auto", paddingBottom: "24px" }}
            >
                <motion.div variants={MOTION.item} style={styles.hero(HERO_GRADIENT)}>
                    <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                            <h5 className="fw-bold mb-0 d-flex align-items-center gap-1">
                                <MdListAlt size={22} />
                                Recovery trades
                            </h5>
                            <p className="mb-0 small opacity-75">
                                {meta.total > 0
                                    ? `${meta.total} total record${meta.total !== 1 ? "s" : ""}`
                                    : "Your recovery trade history"}
                            </p>
                        </div>
                        <motion.button
                            type="button"
                            className="btn btn-sm d-flex align-items-center"
                            style={{
                                background: "rgba(255,255,255,0.2)",
                                border: "1px solid rgba(255,255,255,0.3)",
                                color: "#fff",
                                borderRadius: "10px",
                            }}
                            whileTap={{ scale: 0.96 }}
                            onClick={handleRefresh}
                            disabled={loading || refreshing}
                            aria-label="Refresh list"
                        >
                            <motion.span
                                animate={{ rotate: refreshing ? 360 : 0 }}
                                transition={{
                                    duration: 0.8,
                                    repeat: refreshing ? Infinity : 0,
                                    ease: "linear",
                                }}
                            >
                                <MdRefresh size={20} />
                            </motion.span>
                        </motion.button>
                    </div>
                </motion.div>

                {error && !loading && (
                    <motion.div variants={MOTION.item}>
                        <div className="p-3 text-center" style={styles.card}>
                            <p className="mb-3" style={{ color: styles.textMuted }}>
                                {error}
                            </p>
                            <motion.button
                                type="button"
                                className="btn"
                                style={styles.submitBtn}
                                whileTap={{ scale: 0.98 }}
                                onClick={handleRefresh}
                            >
                                Try again
                            </motion.button>
                        </div>
                    </motion.div>
                )}

                {loading && (
                    <motion.div variants={MOTION.item} className="text-center py-5">
                        <div
                            className="spinner-border text-success mb-2"
                            role="status"
                        />
                        <p className="mb-0 small" style={{ color: styles.textMuted }}>
                            Loading recovery trades…
                        </p>
                    </motion.div>
                )}

                {!loading && !error && trades.length === 0 && (
                    <motion.div variants={MOTION.item} style={styles.empty}>
                        <MdListAlt size={40} className="mb-2 opacity-50" />
                        <p className="mb-2 fw-semibold">No recovery trades yet</p>
                        <p className="mb-3 small">
                            Create your first recovery trade to see it here.
                        </p>
                        <motion.button
                            type="button"
                            className="btn btn-sm"
                            style={{
                                ...styles.submitBtn,
                                background:
                                    "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                boxShadow: "0 6px 20px rgba(5, 150, 105, 0.35)",
                            }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => navigate("/create-recovery-trade")}
                        >
                            Create recovery trade
                        </motion.button>
                    </motion.div>
                )}

                {!loading && !error && trades.length > 0 && (
                    <div className="d-flex flex-column gap-3">
                {trades.map((trade, index) => (
                        <div
                            key={`${trade.recovery_id}-${index}`}
                            style={styles.card}
                            className="p-3"
                        >
                            <div className="d-flex align-items-center gap-2 mb-3">
                                <MdCalendarToday
                                    size={18}
                                    style={{ color: "#059669", flexShrink: 0 }}
                                />
                                <p className="mb-0 fw-semibold" style={{ fontSize: "0.9rem" }}>
                                    {trade.create_date
                                        ? moment(trade.create_date).format(
                                              "DD MMM YYYY, hh:mm A"
                                          )
                                        : "—"}
                                </p>
                            </div>

                            <div className="row g-2">
                                {amountFields.map(({ key, label, icon: Icon, accent }) => (
                                    <div key={key} className="col-6">
                                        <div
                                            className="p-2 h-100"
                                            style={{
                                                borderRadius: "10px",
                                                backgroundColor: isDark
                                                    ? "#0f172a"
                                                    : "#f8fafc",
                                                border: `1px solid ${isDark ? "#1f2937" : "#e5e7eb"}`,
                                            }}
                                        >
                                            <div className="d-flex align-items-center gap-1 mb-1">
                                                <Icon size={14} style={{ color: accent }} />
                                                <span
                                                    className="text-uppercase"
                                                    style={{
                                                        fontSize: "0.6rem",
                                                        fontWeight: 700,
                                                        color: styles.textMuted,
                                                    }}
                                                >
                                                    {label}
                                                </span>
                                            </div>
                                            <p
                                                className="mb-0 fw-bold"
                                                style={{ fontSize: "0.95rem", color: accent }}
                                            >
                                                {formatAmount(trade[key])}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                    </div>
                )}

                <div ref={loadMoreRef} style={{ height: 1 }} />

                {loadingMore && (
                    <div className="text-center py-2">
                        <div
                            className="spinner-border spinner-border-sm text-success"
                            role="status"
                        />
                    </div>
                )}

                {!loading && !loadingMore && !error && trades.length > 0 && (
                    <p
                        className="text-center mb-0 small"
                        style={{ color: styles.textMuted }}
                    >
                        {meta.has_more
                            ? `Page ${meta.page_no} of ${meta.total_page} · scroll for more`
                            : `Showing all ${trades.length} of ${meta.total || trades.length}`}
                    </p>
                )}
            </motion.div>
        </motion.div>
    );
};

export default RecoveryTradeList;
